import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import {
  BarChart3,
  Copy,
  Eye,
  EyeOff,
  KeyRound,
  Plus,
  Trash2
} from "lucide-react"
import { DateTime } from "luxon"
import { selectUserDetails } from "../../redux/slices/auth"
import { useAppDispatch, useAppSelector } from "../../common/hooks"
import { useIsSuperAdmin } from "../../common/hooks/use-user-roles"
import { pushToast } from "../../redux/slices/notifications"
import { PrimaryBtn, LightBtn } from "../../common/ui/buttons"
import BBModal from "../../common/ui/modal"
import EmptyState from "../../common/ui/empty-state"
import { ListSkeleton } from "../../common/ui/skeletons"
import LoanOfficerAutocomplete from "../../common/ui/loan-officer-autocomplete"
import { APIKeyRespose, LoanOfficer } from "../../types"
import bbApi from "../../api"
import { AdminApiKey } from "../../api/api-key"
import { ApiKeyUsageByKey } from "../../api/api-usage"

const maskKey = (key: string) => {
  if (!key || key.length <= 12) return key || "\u2014"
  return key.slice(0, 6) + "\u2022".repeat(8) + key.slice(-6)
}

const formatCreated = (ts: string) => {
  if (!ts) return "\u2014"
  const num = Number(ts)
  if (isNaN(num)) return ts
  const dt = num > 1e12 ? DateTime.fromMillis(num) : DateTime.fromSeconds(num)
  return dt.isValid ? dt.toFormat("LLL dd, yyyy") : ts
}

interface ApiKeysSectionProps {
  onViewUsage?: (keyId: string) => void
}

const ApiKeysSection = ({ onViewUsage }: ApiKeysSectionProps) => {
  const dispatch = useAppDispatch()
  const user = useAppSelector(selectUserDetails)
  const isSuperAdmin = useIsSuperAdmin()

  // Non-admin: user's own keys
  const [keys, setKeys] = useState<APIKeyRespose[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [revealedKeys, setRevealedKeys] = useState<Set<string>>(new Set())

  // Admin: all keys + usage
  const [adminKeys, setAdminKeys] = useState<AdminApiKey[]>([])
  const [keyUsageMap, setKeyUsageMap] = useState<Map<string, ApiKeyUsageByKey>>(
    new Map()
  )
  const [usageDays, setUsageDays] = useState(30)
  const [sortBy, setSortBy] = useState<"hits" | "last_active" | "org">("hits")
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc")

  // Delete modal
  const [deleteTarget, setDeleteTarget] = useState<{
    id: string
    display: string
  } | null>(null)
  const [deleteKeyword, setDeleteKeyword] = useState("")
  const deleteInputRef = useRef<HTMLInputElement>(null)

  // Generate modal (admin)
  const [showGenerateModal, setShowGenerateModal] = useState(false)
  const [generateForLoid, setGenerateForLoid] = useState("")
  const [generateForLo, setGenerateForLo] = useState<LoanOfficer | null>(null)
  const [isGenerating, setIsGenerating] = useState(false)

  // Fetch current user's keys (non-admin)
  const fetchKeys = useCallback(async () => {
    if (!user?.id) return
    setIsLoading(true)
    try {
      const data = await bbApi.apiKey.getAll(user.id)
      setKeys(data)
    } catch {
      setKeys([])
    } finally {
      setIsLoading(false)
    }
  }, [user?.id])

  // Fetch admin keys (fast)
  const fetchAdminKeys = useCallback(async () => {
    setIsLoading(true)
    try {
      const data = await bbApi.apiKey.getAllAdmin()
      setAdminKeys(data.api_keys ?? [])
    } catch {
      setAdminKeys([])
    } finally {
      setIsLoading(false)
    }
  }, [])

  // Fetch usage stats separately (slow — loads in background)
  const [isLoadingUsage, setIsLoadingUsage] = useState(false)
  useEffect(() => {
    if (!isSuperAdmin) return
    let cancelled = false
    const fetchUsage = async () => {
      setIsLoadingUsage(true)
      try {
        const data = await bbApi.apiUsage.getByKey({ days: usageDays })
        if (!cancelled) {
          const map = new Map<string, ApiKeyUsageByKey>()
          data.forEach(u => map.set(u.api_key_id, u))
          setKeyUsageMap(map)
        }
      } catch {
        if (!cancelled) setKeyUsageMap(new Map())
      } finally {
        if (!cancelled) setIsLoadingUsage(false)
      }
    }
    fetchUsage()
    return () => {
      cancelled = true
    }
  }, [isSuperAdmin, usageDays])

  useEffect(() => {
    if (isSuperAdmin) {
      fetchAdminKeys()
    } else {
      fetchKeys()
    }
  }, [isSuperAdmin, fetchKeys, fetchAdminKeys])

  const handleCopy = (key: string) => {
    navigator.clipboard.writeText(key)
    dispatch(
      pushToast({ msg: "API key copied to clipboard", theme: "success" })
    )
  }

  const toggleReveal = (id: string) => {
    setRevealedKeys(prev => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const handleGenerate = async (loid?: string) => {
    const targetLoid = loid || user?.id
    if (!targetLoid) return
    setIsGenerating(true)
    try {
      await bbApi.apiKey.generate(targetLoid)
      dispatch(pushToast({ msg: "API key generated", theme: "success" }))
      if (isSuperAdmin) {
        await fetchAdminKeys()
      } else {
        await fetchKeys()
      }
      setShowGenerateModal(false)
      setGenerateForLoid("")
      setGenerateForLo(null)
    } catch {
      dispatch(
        pushToast({ msg: "Failed to generate API key", theme: "danger" })
      )
    } finally {
      setIsGenerating(false)
    }
  }

  const handleDelete = async () => {
    if (!deleteTarget) return
    try {
      await bbApi.apiKey.delete(deleteTarget.id)
      dispatch(pushToast({ msg: "API key deleted", theme: "success" }))
      setDeleteTarget(null)
      setDeleteKeyword("")
      if (isSuperAdmin) {
        await fetchAdminKeys()
      } else {
        await fetchKeys()
      }
    } catch {
      dispatch(pushToast({ msg: "Failed to delete API key", theme: "danger" }))
    }
  }

  const formatTimestamp = (ts: string) => {
    if (!ts) return "\u2014"
    const ms = Number(ts)
    if (isNaN(ms)) return ts
    const dt = DateTime.fromMillis(ms)
    return dt.isValid ? dt.toFormat("LLL dd, yyyy") : ts
  }

  const toggleSort = (col: typeof sortBy) => {
    if (sortBy === col) {
      setSortDir(d => (d === "desc" ? "asc" : "desc"))
    } else {
      setSortBy(col)
      setSortDir("desc")
    }
  }

  const sortedAdminKeys = useMemo(() => {
    const sorted = [...adminKeys].sort((a, b) => {
      let cmp = 0
      if (sortBy === "hits") {
        const aHits = parseInt(keyUsageMap.get(a.id)?.hits ?? "0", 10) || 0
        const bHits = parseInt(keyUsageMap.get(b.id)?.hits ?? "0", 10) || 0
        cmp = aHits - bHits
      } else if (sortBy === "last_active") {
        const aTs = Number(keyUsageMap.get(a.id)?.last_seen ?? "0") || 0
        const bTs = Number(keyUsageMap.get(b.id)?.last_seen ?? "0") || 0
        cmp = aTs - bTs
      } else if (sortBy === "org") {
        cmp = (a.org_name || "").localeCompare(b.org_name || "")
      }
      return sortDir === "desc" ? -cmp : cmp
    })
    return sorted
  }, [adminKeys, keyUsageMap, sortBy, sortDir])

  const SortArrow = ({ col }: { col: typeof sortBy }) =>
    sortBy === col ? (
      <span className="ml-1 text-primary-400">
        {sortDir === "desc" ? "\u2193" : "\u2191"}
      </span>
    ) : null

  const TH_CLASS =
    "px-3 py-2 text-left uppercase text-neutral-400 text-xs font-medium"
  const TH_SORTABLE = `${TH_CLASS} cursor-pointer hover:text-neutral-600 select-none`
  const TD_CLASS = "px-3 py-2 text-sm"

  if (isLoading) {
    return <ListSkeleton noOfRows={3} noOfColumns={4} />
  }

  // ──────────────────────────────────────────────
  // Super admin view
  // ──────────────────────────────────────────────
  if (isSuperAdmin) {
    return (
      <>
        <div className="bg-white rounded-lg border border-neutral-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-neutral-100 flex items-center justify-between">
            <div>
              <h3 className="text-base font-semibold text-neutral-800">
                All API Keys
              </h3>
              <p className="text-xs text-neutral-400 mt-0.5">
                {adminKeys.length} key
                {adminKeys.length !== 1 ? "s" : ""} across the system
              </p>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex rounded-md border border-neutral-200 overflow-hidden">
                {[7, 30, 90].map(d => (
                  <button
                    key={d}
                    onClick={() => setUsageDays(d)}
                    className={`px-3 py-1 text-xs font-medium transition-colors ${
                      usageDays === d
                        ? "bg-primary-400 text-white"
                        : "bg-white text-neutral-600 hover:bg-neutral-50"
                    }`}
                  >
                    {d}d
                  </button>
                ))}
              </div>
              <PrimaryBtn onClick={() => setShowGenerateModal(true)} size="sm">
                <Plus size={14} className="mr-1.5" />
                Generate Key
              </PrimaryBtn>
            </div>
          </div>

          {adminKeys.length === 0 ? (
            <div className="px-6 py-8 text-center text-neutral-500">
              No API keys in the system.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="border-b border-zinc-200">
                    <th className={TH_CLASS}>Key</th>
                    <th className={TH_CLASS}>Owner</th>
                    <th
                      className={TH_SORTABLE}
                      onClick={() => toggleSort("org")}
                    >
                      Organization
                      <SortArrow col="org" />
                    </th>
                    <th
                      className={TH_SORTABLE}
                      onClick={() => toggleSort("hits")}
                    >
                      Hits
                      <SortArrow col="hits" />
                    </th>
                    <th
                      className={TH_SORTABLE}
                      onClick={() => toggleSort("last_active")}
                    >
                      Last Active
                      <SortArrow col="last_active" />
                    </th>
                    <th className={TH_CLASS}>Status</th>
                    <th className={`${TH_CLASS} text-right`}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedAdminKeys.map(key => {
                    const usage = keyUsageMap.get(key.id)
                    const hits = usage ? parseInt(usage.hits, 10) || 0 : 0
                    return (
                      <tr
                        key={key.id}
                        className="border-b border-zinc-200 hover:bg-neutral-50 transition-colors"
                      >
                        <td className={`${TD_CLASS} whitespace-nowrap`}>
                          <span className="font-mono text-neutral-700">
                            ...{key.key_suffix}
                          </span>
                          {key.key_name && (
                            <span className="ml-2 text-neutral-400 text-xs">
                              {key.key_name}
                            </span>
                          )}
                        </td>
                        <td className={`${TD_CLASS} whitespace-nowrap`}>
                          <span className="text-neutral-800">
                            {key.owner_name}
                          </span>
                        </td>
                        <td className={`${TD_CLASS} text-neutral-500`}>
                          {key.org_name}
                        </td>
                        <td
                          className={`${TD_CLASS} font-mono whitespace-nowrap ${
                            hits > 0
                              ? "font-medium text-neutral-800"
                              : "text-neutral-400"
                          }`}
                        >
                          {isLoadingUsage ? (
                            <span className="inline-block h-4 w-12 bg-neutral-200 rounded animate-pulse" />
                          ) : hits > 0 ? (
                            hits.toLocaleString()
                          ) : (
                            "\u2014"
                          )}
                        </td>
                        <td
                          className={`${TD_CLASS} text-neutral-500 whitespace-nowrap`}
                        >
                          {isLoadingUsage ? (
                            <span className="inline-block h-4 w-24 bg-neutral-200 rounded animate-pulse" />
                          ) : usage?.last_seen ? (
                            formatTimestamp(usage.last_seen)
                          ) : (
                            "\u2014"
                          )}
                        </td>
                        <td className={TD_CLASS}>
                          <span className="inline-flex items-center gap-1.5 text-sm">
                            <span
                              className={`w-2 h-2 rounded-full ${
                                key.status === "1"
                                  ? "bg-green-500"
                                  : "bg-neutral-400"
                              }`}
                            />
                            {key.status === "1" ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td className={`${TD_CLASS} whitespace-nowrap`}>
                          <div className="flex items-center justify-end gap-1">
                            {onViewUsage && (
                              <button
                                onClick={() => onViewUsage(key.id)}
                                className="p-1.5 rounded-md hover:bg-primary-50 transition-colors"
                                title="View usage"
                              >
                                <BarChart3
                                  size={15}
                                  className="text-neutral-500 hover:text-primary-400"
                                />
                              </button>
                            )}
                            <button
                              onClick={() =>
                                setDeleteTarget({
                                  id: key.id,
                                  display: `...${key.key_suffix}`
                                })
                              }
                              className="p-1.5 rounded-md hover:bg-red-50 transition-colors"
                              title="Delete key"
                            >
                              <Trash2
                                size={15}
                                className="text-neutral-500 hover:text-red-500"
                              />
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Generate key modal (admin - pick a user) */}
        <BBModal
          size="md"
          show={showGenerateModal}
          onClose={() => {
            setShowGenerateModal(false)
            setGenerateForLoid("")
            setGenerateForLo(null)
          }}
          title="Generate API Key"
        >
          <p className="text-sm text-neutral-600 mb-4">
            Select a loan officer to generate an API key for.
          </p>
          <div className={`mb-4 ${!generateForLo ? "min-h-[220px]" : ""}`}>
            <LoanOfficerAutocomplete
              label="Loan officer"
              direction="vertical"
              selected={generateForLoid}
              showBrandName
              showSearchToggle
              onChange={setGenerateForLoid}
              onSelectLo={setGenerateForLo}
            />
          </div>
          {generateForLo && (
            <div className="mb-4 p-3 bg-neutral-50 rounded-md border border-neutral-200">
              <p className="text-sm font-medium text-neutral-700">
                {generateForLo.name}
              </p>
              <p className="text-xs text-neutral-400">
                {generateForLo.email}
                {generateForLo.nmls && ` \u00B7 NMLS ${generateForLo.nmls}`}
              </p>
            </div>
          )}
          <div className="flex justify-end items-center gap-3">
            <LightBtn
              onClick={() => {
                setShowGenerateModal(false)
                setGenerateForLoid("")
                setGenerateForLo(null)
              }}
            >
              Cancel
            </LightBtn>
            <PrimaryBtn
              onClick={() => handleGenerate(generateForLoid)}
              disabled={!generateForLoid || isGenerating}
            >
              {isGenerating ? "Generating..." : "Generate"}
            </PrimaryBtn>
          </div>
        </BBModal>

        {/* Delete confirmation modal */}
        <BBModal
          size="sm"
          show={!!deleteTarget}
          onClose={() => {
            setDeleteTarget(null)
            setDeleteKeyword("")
          }}
          title="Delete API Key"
          initialFocus={deleteInputRef}
        >
          <p className="text-sm text-neutral-600 mb-2">
            This action cannot be undone. Any applications using this key will
            lose access immediately.
          </p>
          <p className="text-sm text-neutral-500 font-mono mb-4">
            {deleteTarget?.display ?? ""}
          </p>
          <div className="mb-4">
            <label
              htmlFor="deleteKeyword"
              className="mb-1 inline-block text-sm"
            >
              Type "YES" to confirm
            </label>
            <input
              id="deleteKeyword"
              ref={deleteInputRef}
              onChange={e => setDeleteKeyword(e.target.value)}
              className="px-4 py-3 text-base leading-6 border-2 w-full border-zinc-200 rounded-sm focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5"
              value={deleteKeyword}
            />
          </div>
          <div className="flex justify-end items-center gap-3">
            <LightBtn
              onClick={() => {
                setDeleteTarget(null)
                setDeleteKeyword("")
              }}
            >
              Cancel
            </LightBtn>
            <PrimaryBtn
              onClick={handleDelete}
              disabled={deleteKeyword !== "YES"}
            >
              Delete
            </PrimaryBtn>
          </div>
        </BBModal>
      </>
    )
  }

  // ──────────────────────────────────────────────
  // Non-admin view: current user's keys only
  // ──────────────────────────────────────────────
  if (keys.length === 0) {
    return (
      <EmptyState
        icon={KeyRound}
        heading="No API keys"
        subHeading="Generate an API key to access the BankingBridge API"
        isActionButton
        buttonText="Generate API Key"
        onClick={() => handleGenerate()}
      />
    )
  }

  return (
    <>
      <div className="bg-white rounded-lg border border-neutral-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-neutral-100 flex items-center justify-between">
          <h3 className="text-base font-semibold text-neutral-800">API Keys</h3>
          <PrimaryBtn
            onClick={() => handleGenerate()}
            disabled={isGenerating}
            size="sm"
          >
            <Plus size={14} className="mr-1.5" />
            {isGenerating ? "Generating..." : "Generate Key"}
          </PrimaryBtn>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="border-b border-zinc-200">
                <th className={TH_CLASS}>API Key</th>
                <th className={TH_CLASS}>Created</th>
                <th className={TH_CLASS}>Status</th>
                <th className={`${TH_CLASS} text-right`}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {keys.map(key => {
                const isRevealed = revealedKeys.has(key.id)
                return (
                  <tr
                    key={key.id}
                    className="border-b border-zinc-200 hover:bg-neutral-50 transition-colors"
                  >
                    <td className={`${TD_CLASS} font-mono text-neutral-700`}>
                      <span className="select-all">
                        {isRevealed ? key.api_key : maskKey(key.api_key)}
                      </span>
                    </td>
                    <td
                      className={`${TD_CLASS} text-neutral-500 whitespace-nowrap`}
                    >
                      {formatCreated(key.created)}
                    </td>
                    <td className={TD_CLASS}>
                      <span className="inline-flex items-center gap-1.5 text-sm">
                        <span
                          className={`w-2 h-2 rounded-full ${
                            key.status === "1" || !key.status
                              ? "bg-green-500"
                              : "bg-neutral-400"
                          }`}
                        />
                        {key.status === "1" || !key.status
                          ? "Active"
                          : "Inactive"}
                      </span>
                    </td>
                    <td className={`${TD_CLASS} whitespace-nowrap`}>
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => toggleReveal(key.id)}
                          className="p-1.5 rounded-md hover:bg-neutral-100 transition-colors"
                          title={isRevealed ? "Hide key" : "Reveal key"}
                        >
                          {isRevealed ? (
                            <EyeOff size={15} className="text-neutral-500" />
                          ) : (
                            <Eye size={15} className="text-neutral-500" />
                          )}
                        </button>
                        <button
                          onClick={() => handleCopy(key.api_key)}
                          className="p-1.5 rounded-md hover:bg-neutral-100 transition-colors"
                          title="Copy to clipboard"
                        >
                          <Copy size={15} className="text-neutral-500" />
                        </button>
                        <button
                          onClick={() => {
                            setDeleteTarget({
                              id: key.id,
                              display: maskKey(key.api_key)
                            })
                            setDeleteKeyword("")
                          }}
                          className="p-1.5 rounded-md hover:bg-red-50 transition-colors"
                          title="Delete key"
                        >
                          <Trash2
                            size={15}
                            className="text-neutral-500 hover:text-red-500"
                          />
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Delete confirmation modal */}
      <BBModal
        size="sm"
        show={!!deleteTarget}
        onClose={() => {
          setDeleteTarget(null)
          setDeleteKeyword("")
        }}
        title="Delete API Key"
        initialFocus={deleteInputRef}
      >
        <p className="text-sm text-neutral-600 mb-2">
          This action cannot be undone. Any applications using this key will
          lose access immediately.
        </p>
        <p className="text-sm text-neutral-500 font-mono mb-4">
          {deleteTarget?.display ?? ""}
        </p>
        <div className="mb-4">
          <label htmlFor="deleteKeyword" className="mb-1 inline-block text-sm">
            Type "YES" to confirm
          </label>
          <input
            id="deleteKeyword"
            ref={deleteInputRef}
            onChange={e => setDeleteKeyword(e.target.value)}
            className="px-4 py-3 text-base leading-6 border-2 w-full border-zinc-200 rounded-sm focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5"
            value={deleteKeyword}
          />
        </div>
        <div className="flex justify-end items-center gap-3">
          <LightBtn
            onClick={() => {
              setDeleteTarget(null)
              setDeleteKeyword("")
            }}
          >
            Cancel
          </LightBtn>
          <PrimaryBtn onClick={handleDelete} disabled={deleteKeyword !== "YES"}>
            Delete
          </PrimaryBtn>
        </div>
      </BBModal>
    </>
  )
}

export default ApiKeysSection
